import { useState, useCallback, useMemo } from 'react';
import { CareConciergeFormData } from '@/features/provider-directory/components/CareConciergContactForm/types';

interface DraftData {
  data: Partial<CareConciergeFormData>;
  timestamp: number;
}

const DRAFT_EXPIRY_DAYS = 7;

export function useCareConciergeModal(userId: string) {
  const STORAGE_KEY = useMemo(() => `care-concierge-draft-${userId}`, [userId]);
  const [hasDraft, setHasDraft] = useState<boolean>(false);

  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setHasDraft(false);
    } catch (error) {
      console.error('Error clearing draft from localStorage:', error);
    }
  }, [STORAGE_KEY]);

  const saveDraft = useCallback((formData: Partial<CareConciergeFormData>) => {
    try {
      const draftData: DraftData = {
        data: formData,
        timestamp: Date.now(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(draftData));
      setHasDraft(true);
    } catch (error) {
      console.error('Error saving draft to localStorage:', error);
    }
  }, [STORAGE_KEY]);

  const getDraft = useCallback((): { data: Partial<CareConciergeFormData>; timestamp: number } | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return null;
      }

      const parsed: DraftData = JSON.parse(stored);
      const age = Date.now() - parsed.timestamp;
      const maxAge = DRAFT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

      // Discard drafts older than 7 days
      if (age > maxAge) {
        clearDraft();
        return null;
      }

      return { data: parsed.data, timestamp: parsed.timestamp };
    } catch (error) {
      console.error('Error retrieving draft from localStorage:', error);
      return null;
    }
  }, [STORAGE_KEY, clearDraft]);

  return {
    saveDraft,
    getDraft,
    clearDraft,
    hasDraft,
  };
}
